/**
 * InteractionIntentModal - Capture exact player intent for narrative actions.
 * date_of_change: 2026-07-18
 */
import React, { useEffect, useRef, useState } from 'react';
import BattleRoomDialog from './BattleRoomDialog';

const DEFAULT_TARGET = '__audible__';

const InteractionIntentModal = ({
  visible = false,
  title = 'Describe interaction',
  description = 'Record the exact intent GMA should resolve.',
  textLabel = 'What are you trying to do?',
  placeholder = '',
  confirmLabel = 'Record intent',
  footerNote = 'This intent will be included in the GMA narration handoff.',
  targets = [],
  initialTargetId = DEFAULT_TARGET,
  untargetedLabel = 'Everyone who can hear',
  onConfirm,
  onCancel
}) => {
  const [text, setText] = useState('');
  const [targetId, setTargetId] = useState(initialTargetId || DEFAULT_TARGET);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    setText('');
    setTargetId(initialTargetId || DEFAULT_TARGET);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [visible, initialTargetId]);

  if (!visible) return null;

  const trimmedText = text.trim();
  const selectedTarget = targets.find((target) => target.id === targetId) || null;
  const submit = (event) => {
    event?.preventDefault?.();
    if (!trimmedText) return;
    onConfirm?.({
      text: trimmedText,
      targetId: selectedTarget?.id || null,
      targetName: selectedTarget?.name || null
    });
  };

  return (
    <BattleRoomDialog
      visible={visible}
      eyebrow="Scene intent"
      title={title}
      description={description}
      onRequestClose={onCancel}
    >
      <form onSubmit={submit}>
        <div className="battle-room-dialog__field">
          <label className="battle-room-dialog__label" htmlFor="interaction-intent-target">Target or audience</label>
          <select
            id="interaction-intent-target"
            aria-label="Target or audience"
            className="battle-room-dialog__select"
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
          >
            <option value={DEFAULT_TARGET}>{untargetedLabel}</option>
            {targets.map((target) => (
              <option key={target.id} value={target.id}>{target.name}</option>
            ))}
          </select>
        </div>

        <div className="battle-room-dialog__field">
          <label className="battle-room-dialog__label" htmlFor="interaction-intent-text">{textLabel}</label>
          <textarea
            ref={textareaRef}
            id="interaction-intent-text"
            aria-label={textLabel}
            className="battle-room-dialog__textarea"
            rows={4}
            maxLength={500}
            placeholder={placeholder}
            value={text}
            data-battle-dialog-autofocus
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') submit(event);
            }}
          />
          <p className="battle-room-dialog__note">{footerNote} Ctrl+Enter records it.</p>
        </div>

        <div className="battle-room-dialog__actions">
          <button
            type="button"
            className="battle-room-dialog__button battle-room-dialog__button--quiet"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!trimmedText}
            className="battle-room-dialog__button battle-room-dialog__button--primary"
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </BattleRoomDialog>
  );
};

export default InteractionIntentModal;
